import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import './SchoolList.css';
const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const SchoolList = () => {
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    /* ---------------- CLASS MODAL STATE ---------------- */
    const [showClassModal, setShowClassModal] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [classInputs, setClassInputs] = useState([""]);
    const [classLoading, setClassLoading] = useState(false);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const [modalData, setModalData] = useState({
        id: null,
        school_name: "",
        school_location: "",
        school_address: "",
        school_contact: "",
        school_description: "",
        school_image_file: null,
        school_image_preview: null,
        old_school_image: null,
    });

    const [galleryImages, setGalleryImages] = useState([]);

    /* ---------------- FETCH DATA ---------------- */
    useEffect(() => {
        fetchContent();
    }, []);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${baseAPIUrl}admin/general_settings/get_schools/`,
                { withCredentials: true }
            );

            if (res.data.status === 1 && res.data.data) {
                setGalleryImages(res.data.data || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- Handler --------------- */

    const handleEdit = (item) => {
        setIsEdit(true);
        setModalData({
            id: item.id,
            school_name: item.school_name || "",
            school_location: item.school_location || "",
            school_address: item.school_address || "",
            school_contact: item.school_contact || "",
            school_description: item.school_description || "",
            school_image_file: null,
            school_image_preview: `${baseAPIUrl}assets/images/schools/${item.school_image}`,
            old_school_image: item.school_image,
        });
        setShowModal(true);
    };


    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setModalData(prev => ({
                ...prev,
                school_image_file: file,
                school_image_name: file.name,   // ✅ auto image name
                school_image_preview: URL.createObjectURL(file),
            }));
        }
    };


    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this school?")) return;
        const formData = new FormData();
        formData.append("id", id);
        try {
            const res = await axios.post(
                `${baseAPIUrl}admin/general_settings/delete_school`,
                formData,
                { withCredentials: true }
            );

            // Remove from UI instantly
            if (res.data.status === 1) {
                setGalleryImages(prev => prev.filter(img => img.id !== id));
            } else {
                alert(res.data.message || "Delete failed");
            }
        } catch (err) {
            console.error(err.response);
            alert(err.response?.data?.message || "Failed to delete school");
        }
    };

    const handleSave = async () => {
        const formData = new FormData();

        formData.append("school_name", modalData.school_name);
        formData.append("school_location", modalData.school_location);
        formData.append("school_address", modalData.school_address);
        formData.append("school_contact", modalData.school_contact);
        formData.append("school_description", modalData.school_description);

        if (modalData.school_image_file) {
            formData.append("school_image", modalData.school_image_file);
        }

        if (isEdit) {
            formData.append("id", modalData.id);
            formData.append("old_school_image", modalData.old_school_image);
        }

        try {
            const url = isEdit
                ? `${baseAPIUrl}admin/general_settings/update_school`
                : `${baseAPIUrl}admin/general_settings/add_school`;

            await axios.post(url, formData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });

            setShowModal(false);
            fetchContent(); // reload table
        } catch (err) {
            console.error(err);
            alert("Failed to save image");
        }
    };

    /* ---------------- ADD CLASS HANDLERS ---------------- */

    const handleAddClass = async (school) => {
        setSelectedSchool(school);
        setShowClassModal(true);
        setClassLoading(true);

        try {
            const res = await axios.get(
                `${baseAPIUrl}admin/general_settings/get_classes/${school.id}`,
                { withCredentials: true }
            );

            if (res.data.status === 1 && res.data.data.length > 0) {
                const existingClasses = res.data.data.map(c => c.class_name);
                setClassInputs(existingClasses);
            } else {
                setClassInputs([""]);
            }

        } catch (error) {
            console.error(error);
            setClassInputs([""]);
        }

        setClassLoading(false);
    };

    const handleAddMoreField = () => {
        setClassInputs([...classInputs, ""]);
    };

    const handleRemoveField = (index) => {
        if (classInputs.length === 1) {
            alert("At least one class is required");
            return;
        }

        const updated = classInputs.filter((_, i) => i !== index);
        setClassInputs(updated);
    };

    const handleClassChange = (value, index) => {
        const updated = [...classInputs];
        updated[index] = value;
        setClassInputs(updated);
    };

    const handleSaveClasses = async () => {
        const filtered = classInputs.filter(c => c.trim() !== "");

        if (filtered.length === 0) {
            alert("Please enter at least one class name");
            return;
        }

        setClassLoading(true);

        try {
            await axios.post(
                `${baseAPIUrl}admin/general_settings/add_classes`,
                {
                    school_id: selectedSchool.id,
                    classes: filtered
                },
                { withCredentials: true }
            );

            alert("Classes added successfully");
            setShowClassModal(false);

        } catch (error) {
            console.error(error);
            alert("Failed to save classes");
        }

        setClassLoading(false);
    };



    /* ---------------- Submit ---------------- */


    if (loading) return <p>Loading...</p>;
    return (
        <div className="content-wrapper">
            {/* Content Header (Page header) */}
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 text-dark">School List</h1>
                        </div>{/* /.col */}
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/admin/dashboard">Dashboard</a></li>
                                <li class="breadcrumb-item active">School List</li>
                            </ol>
                        </div>{/* /.col */}
                    </div>{/* /.row */}
                </div>{/* /.container-fluid */}
            </div>
            {/* /.content-header */}
            {/* Main content */}
            <section className="content">
                <div className="container-fluid">
                    <div class="row">
                        <div class="col-12">
                            <div class="card">
                                {/*-- /.card-header --*/}
                                <div class="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4>School list</h4>
                                        </div>
                                        <div className="col-md-6 text-right">
                                            <button className="btn btn-primary" onClick={() => {
                                                setIsEdit(false);
                                                setModalData({
                                                    id: null,
                                                    school_name: "",
                                                    school_location: "",
                                                    school_address: "",
                                                    school_contact: "",
                                                    school_description: "",
                                                    school_image_file: null,
                                                    school_image_preview: null,
                                                    old_school_image: null,
                                                });
                                                setShowModal(true);
                                            }}>Add School</button>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body table-responsive p-0">
                                    <table className="table table-hover text-nowrap">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Image</th>
                                                <th>Schhol Name</th>
                                                <th>Schol Slug</th>
                                                <th>Location</th>
                                                <th>Address</th>
                                                <th>Contact</th>
                                                <th>Description</th>
                                                <th style={{ width: "150px" }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {galleryImages.length > 0 ? (
                                                galleryImages.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <img
                                                                src={`${baseAPIUrl}assets/images/schools/${item.school_image}`}
                                                                alt="School"
                                                                style={{
                                                                    width: "120px",
                                                                    height: "80px",
                                                                    objectFit: "cover",
                                                                    borderRadius: "6px",
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            {item.school_name ? item.school_name : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            {item.school_slug ? item.school_slug : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            {item.school_location ? item.school_location : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            {item.school_address?.length > 50
                                                                ? item.school_address.slice(0, 50) + "..."
                                                                : item.school_address}
                                                        </td>
                                                        <td>
                                                            {item.school_contact ? item.school_contact : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            {item.school_description ? item.school_description : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-primary mr-2"
                                                                onClick={() => handleAddClass(item)}
                                                                title="Add Classes"
                                                            >
                                                                <i className="fas fa-plus"></i>
                                                            </button>

                                                            <button
                                                                className="btn btn-sm btn-info mr-2"
                                                                onClick={() => handleEdit(item)}
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>

                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDelete(item.id)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center">
                                                        No Data found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {showModal && (
                <div
                    className="modal fade show"
                    style={{
                        display: "flex",
                        alignItems: "center",      // ✅ vertical center
                        justifyContent: "center",  // ✅ horizontal center
                        backgroundColor: "rgba(0,0,0,0.5)",
                        inset: 0,
                        zIndex: 1050,
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: "80%", margin: 0, width: "100%" }}>
                        <div className="modal-content" style={{ maxWidth: "80%", margin: "0 auto", width: "100%" }}>
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {isEdit ? "Edit School" : "Add School"}
                                </h5>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() => setShowModal(false)}
                                >
                                    <span>&times;</span>
                                </button>
                            </div>

                            <div className="modal-body">

                                {/* School name */}
                                <div className="form-group">
                                    <label>School name</label>
                                    <input
                                        className="form-control"
                                        rows="3"
                                        value={modalData.school_name}
                                        onChange={(e) =>
                                            setModalData({ ...modalData, school_name: e.target.value })
                                        }
                                    />
                                </div>

                                {/* School location */}
                                <div className="form-group">
                                    <label>School location</label>
                                    <input
                                        className="form-control"
                                        rows="3"
                                        value={modalData.school_location}
                                        onChange={(e) =>
                                            setModalData({ ...modalData, school_location: e.target.value })
                                        }
                                    />
                                </div>

                                {/* School address */}
                                <div className="form-group">
                                    <label>School address</label>
                                    <input
                                        className="form-control"
                                        rows="3"
                                        value={modalData.school_address}
                                        onChange={(e) =>
                                            setModalData({ ...modalData, school_address: e.target.value })
                                        }
                                    />
                                </div>

                                {/* School contact no */}
                                <div className="form-group">
                                    <label>School contact no</label>
                                    <input
                                        className="form-control"
                                        rows="3"
                                        value={modalData.school_contact}
                                        onChange={(e) =>
                                            setModalData({ ...modalData, school_contact: e.target.value })
                                        }
                                    />
                                </div>

                                {/* School description */}
                                <div className="form-group">
                                    <label>School description</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={modalData.school_description}
                                        onChange={(e) =>
                                            setModalData({ ...modalData, school_description: e.target.value })
                                        }
                                    ></textarea>
                                </div>

                                {/* Image Upload */}
                                <div className="form-group">
                                    <label>School image</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                </div>

                                {/* Preview */}
                                {modalData.school_image_preview && (
                                    <div className="mt-3">
                                        <img
                                            src={modalData.school_image_preview}
                                            alt="Preview"
                                            style={{
                                                maxWidth: "150px",
                                                maxHeight: "120px",
                                                borderRadius: "8px",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button className="btn btn-success" onClick={handleSave}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* ================= CLASS MODAL ================= */}
            {showClassModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal">

                        <div className="custom-modal-header">
                            <h5>
                                Add Classes - {selectedSchool?.school_name}
                            </h5>
                            <button
                                className="close-btn"
                                onClick={() => setShowClassModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="custom-modal-body">

                            {classInputs.map((cls, index) => (
                                <div key={index} className="class-row">

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={cls}
                                        placeholder="Enter class name"
                                        onChange={(e) =>
                                            handleClassChange(e.target.value, index)
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={() => handleRemoveField(index)}
                                        disabled={classInputs.length === 1}
                                    >
                                        ✕
                                    </button>

                                </div>
                            ))}

                            <button
                                className="add-more-btn"
                                onClick={handleAddMoreField}
                            >
                                + Add More
                            </button>

                        </div>

                        <div className="custom-modal-footer">
                            <button
                                className="btn btn-light"
                                onClick={() => setShowClassModal(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className="btn btn-success"
                                onClick={handleSaveClasses}
                                disabled={classLoading}
                            >
                                {classLoading ? "Saving..." : "Save Classes"}
                            </button>
                        </div>

                    </div>
                </div>
            )}


        </div >
    )
}
export default SchoolList;